import { GetStaticPaths, GetStaticProps } from 'next';
import { BreadcrumbJsonLd, NextSeo } from 'next-seo';
import { PostOrPage } from '@tryghost/content-api';

import { getPosts, getSinglePost } from '../../client/ghost';
import GhostPost from '../../client/components/blog/GhostPost';

export const ROOT_PATH = '/blog';

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = await getPosts({ filter: 'tag:Blog' });

  return {
    paths: paths?.map((path) => ({ params: { slug: path.slug } })) || [],
    fallback: true,
  };
};

export const getStaticProps: GetStaticProps<PageProps> = async ({ params }) => {
  if (!params?.slug || Array.isArray(params.slug)) return { notFound: true };

  let ghostPost;

  try {
    ghostPost = await getSinglePost(params.slug);
  } catch (e) {
    return { notFound: true };
  }

  if (!ghostPost) return { notFound: true };

  return {
    props: {
      ghostPost: ghostPost,
    },
  };
};

type PageProps = {
  ghostPost: PostOrPage;
};

const BlogPage = ({ ghostPost }: PageProps) => {
  if (!ghostPost) return null;

  const { title } = ghostPost;
  const description = ghostPost.meta_description || '';
  const slug = ghostPost.slug;

  return (
    <>
      <NextSeo
        title={`${title} | Stackblocks Blog`}
        description={description}
        canonical={`https://stackblocks.app${ROOT_PATH}/${slug}`}
        openGraph={{
          url: `https://stackblocks.app${ROOT_PATH}/${slug}`,
          title: `${ghostPost.og_title || title} | Stackblocks Blog`,
          description: ghostPost.og_description || description,
          images: [
            {
              url:
                ghostPost.og_image ||
                ghostPost.feature_image ||
                `https://stackblocks.app/stackblocks-opengraph.png`,
              width: 1200,
              height: 600,
              alt: 'Stackblocks',
            },
          ],
          site_name: 'Stackblocks Webiste',
          type: 'article',
        }}
        twitter={{
          handle: '@aravindballa',
          site: '@StackblocksApp',
          cardType: 'summary_large_image',
        }}
      />
      <BreadcrumbJsonLd
        itemListElements={[
          {
            position: 1,
            name: 'Stackblocks',
            item: `https://stackblocks.app`,
          },
          {
            position: 2,
            name: 'Blog',
            item: `https://stackblocks.app${ROOT_PATH}`,
          },
          {
            position: 3,
            name: title,
            item: `https://stackblocks.app${ROOT_PATH}/${slug}`,
          },
        ]}
      />
      <GhostPost post={ghostPost} />:
    </>
  );
};

export default BlogPage;
